/**
 * PlayerInfoWidget Component for Poker Reborn
 *
 * Reusable widget displaying player info:
 * - Avatar (circle with first letter placeholder or image)
 * - Player name (bold white text)
 * - Balance display (pill shape, clickable to refresh)
 *
 * Used by both LobbyBar and RoomBar for consistent player info display.
 *
 * @see docs/screens/lobby/LOBBY_BAR.md - LobbyBar visual specs
 * @see docs/screens/room/ROOM_BAR.md - RoomBar visual specs
 */

import { Container, Graphics, Text, Sprite, Texture } from 'pixi.js';
import { appState } from '@/state/AppState';
import { accountService } from '@/services/AccountService';
import { formatBalance, formatBalanceCompact, formatGold, formatGoldCompact, formatTickets, formatTicketsCompact } from '@/utils/format';
import { getAvatarPath } from '@/config/avatars';
import type { Unsubscribe } from '@/state/Signal';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  /** Avatar dimensions */
  avatar: {
    size: 32,
    backgroundColor: 0x333333,
    backgroundAlpha: 0.8,
    borderColor: 0xd4af37, // Gold
    borderWidth: 2,
    fontSize: 14,
    fontFamily: 'Roboto, Arial, sans-serif',
    textColor: 0xffffff,
    gap: 10, // Gap after avatar
  },

  /** Name text */
  name: {
    fontSize: 18,
    fontFamily: 'Roboto, Arial, sans-serif',
    textColor: 0xffffff,
    gap: 12, // Gap after name
  },

  /** Balance display */
  balance: {
    backgroundColor: 0xffffff,
    backgroundAlpha: 0.15,
    borderRadius: 16,
    paddingX: 14,
    paddingY: 6,
    fontSize: 14,
    fontFamily: 'Roboto, Arial, sans-serif',
    textColor: 0xffffff,
    minWidth: 80,
  },

  /** Gold currency display */
  gold: {
    backgroundColor: 0xffd700,
    backgroundAlpha: 0.2,
    borderRadius: 16,
    paddingX: 14,
    paddingY: 6,
    fontSize: 14,
    fontFamily: 'Roboto, Arial, sans-serif',
    textColor: 0xffd700,
    minWidth: 60,
    gap: 8,
  },

  /** Tickets currency display */
  tickets: {
    backgroundColor: 0x00bcd4,
    backgroundAlpha: 0.2,
    borderRadius: 16,
    paddingX: 14,
    paddingY: 6,
    fontSize: 14,
    fontFamily: 'Roboto, Arial, sans-serif',
    textColor: 0x00bcd4,
    minWidth: 60,
    gap: 8,
  },

   /** Level badge */
  level: {
    backgroundColor: 0xffffff,
    backgroundAlpha: 0.1,
    borderRadius: 8,
    paddingX: 8,
    paddingY: 3,
    fontSize: 12,
    fontFamily: 'Roboto, Arial, sans-serif',
    textColor: 0xffffff,
    gap: 8,
  },

  /** XP progress bar */
  xpBar: {
    height: 4,
    maxWidth: 160,
    maxWidthMobile: 100,
    trackColor: 0x333333,
    trackAlpha: 0.5,
    fillColor: 0x4CAF50,
    fillColorAlmost: 0xFFC107,
    borderRadius: 2,
    gap: 4,
  },

  /** Responsive breakpoints */
  breakpoints: {
    mobile: 480,
  },
};

// =============================================================================
// BALANCE STATE
// =============================================================================

interface BalanceState {
  loading: boolean;
  error: boolean;
  value: number;
}

// =============================================================================
// OPTIONS
// =============================================================================

export interface PlayerInfoWidgetOptions {
  /** Avatar image URL (optional, shows placeholder if not provided) */
  avatarUrl?: string;
  /** Avatar size in pixels (default: 32) */
  avatarSize?: number;
  /** Initial player name */
  name?: string;
  /** Screen width for responsive behavior */
  screenWidth: number;
}

// =============================================================================
// PLAYER INFO WIDGET
// =============================================================================

export class PlayerInfoWidget extends Container {
  private screenWidth: number;

  // Components
  private avatarContainer: Container;
  private avatarBg: Graphics;
  private avatarText: Text;
  private avatarSprite: Sprite | null = null;
  private avatarMask: Graphics | null = null;
  private nameText: Text;
  private balanceContainer: Container;
  private balanceBg: Graphics;
  private balanceText: Text;
  private goldContainer: Container;
  private goldBg: Graphics;
  private goldText: Text;
  private ticketContainer: Container;
  private ticketBg: Graphics;
  private ticketText: Text;
  private levelBadge: Container;
  private levelBg: Graphics;
  private levelText: Text;
  private xpBarContainer: Container;
  private xpBarBg: Graphics;
  private xpBarFill: Graphics;

  // State
  private currentName: string = '';
  private avatarSize: number;
  private balanceState: BalanceState = {
    loading: true,
    error: false,
    value: 0,
  };
  private goldValue = 0;
  private ticketValue = 0;
  private currentLevel = 1;
  private currentXpPct = 0;

  // Subscriptions
  private unsubscribes: Unsubscribe[] = [];

  // Callbacks
  public onBalanceClick?: () => void;
  public onProfileClick?: () => void;

  constructor(options: PlayerInfoWidgetOptions) {
    super();
    this.screenWidth = options.screenWidth;
    this.avatarSize = options.avatarSize ?? STYLES.avatar.size;
    this.currentName = options.name ?? '';

    // Create avatar container (clickable for profile)
    this.avatarContainer = new Container();
    this.avatarContainer.eventMode = 'static';
    this.avatarContainer.cursor = 'pointer';
    this.avatarContainer.on('pointerdown', () => {
      this.onProfileClick?.();
    });
    this.addChild(this.avatarContainer);

    // Create avatar background (circle)
    this.avatarBg = new Graphics();
    this.avatarContainer.addChild(this.avatarBg);

    // Create avatar text (first letter placeholder)
    this.avatarText = new Text({
      text: '',
      style: {
        fill: STYLES.avatar.textColor,
        fontSize: STYLES.avatar.fontSize,
        fontFamily: STYLES.avatar.fontFamily,
        fontWeight: 'bold',
      },
    });
    this.avatarText.anchor.set(0.5, 0.5);
    this.avatarContainer.addChild(this.avatarText);

    // Create name text (clickable for profile)
    this.nameText = new Text({
      text: this.currentName,
      style: {
        fill: STYLES.name.textColor,
        fontSize: STYLES.name.fontSize,
        fontFamily: STYLES.name.fontFamily,
        fontWeight: 'bold',
      },
    });
    this.nameText.anchor.set(0, 0.5);
    this.nameText.eventMode = 'static';
    this.nameText.cursor = 'pointer';
    this.nameText.on('pointerdown', () => {
      this.onProfileClick?.();
    });
    this.addChild(this.nameText);

    // Create balance container (clickable to refresh)
    this.balanceContainer = new Container();
    this.balanceContainer.eventMode = 'static';
    this.balanceContainer.cursor = 'pointer';
    this.balanceContainer.on('pointerdown', () => {
      this.onBalanceClick?.();
      accountService.requestBalance();
    });
    this.addChild(this.balanceContainer);

    // Create balance background (pill shape)
    this.balanceBg = new Graphics();
    this.balanceContainer.addChild(this.balanceBg);

    // Create balance text
    this.balanceText = new Text({
      text: '...',
      style: {
        fill: STYLES.balance.textColor,
        fontSize: STYLES.balance.fontSize,
        fontFamily: STYLES.balance.fontFamily,
        fontWeight: 'bold',
      },
    });
    this.balanceText.anchor.set(0.5, 0.5);
    this.balanceContainer.addChild(this.balanceText);

    // Create gold container (clickable to refresh)
    this.goldContainer = new Container();
    this.goldContainer.eventMode = 'static';
    this.goldContainer.cursor = 'pointer';
    this.goldContainer.visible = false;
    this.goldContainer.on('pointerdown', () => {
      this.onBalanceClick?.();
      accountService.requestBalance();
    });
    this.addChild(this.goldContainer);

    // Create gold background (pill shape)
    this.goldBg = new Graphics();
    this.goldContainer.addChild(this.goldBg);

    // Create gold text
    this.goldText = new Text({
      text: '',
      style: {
        fill: STYLES.gold.textColor,
        fontSize: STYLES.gold.fontSize,
        fontFamily: STYLES.gold.fontFamily,
        fontWeight: 'bold',
      },
    });
    this.goldText.anchor.set(0.5, 0.5);
    this.goldContainer.addChild(this.goldText);

    // Create ticket container (clickable to refresh)
    this.ticketContainer = new Container();
    this.ticketContainer.eventMode = 'static';
    this.ticketContainer.cursor = 'pointer';
    this.ticketContainer.visible = false;
    this.ticketContainer.on('pointerdown', () => {
      this.onBalanceClick?.();
      accountService.requestBalance();
    });
    this.addChild(this.ticketContainer);

    // Create ticket background (pill shape)
    this.ticketBg = new Graphics();
    this.ticketContainer.addChild(this.ticketBg);

    // Create ticket text
    this.ticketText = new Text({
      text: '',
      style: {
        fill: STYLES.tickets.textColor,
        fontSize: STYLES.tickets.fontSize,
        fontFamily: STYLES.tickets.fontFamily,
        fontWeight: 'bold',
      },
    });
    this.ticketText.anchor.set(0.5, 0.5);
    this.ticketContainer.addChild(this.ticketText);

    // Create level badge (between name and balance)
    this.levelBadge = new Container();
    this.addChild(this.levelBadge);

    this.levelBg = new Graphics();
    this.levelBadge.addChild(this.levelBg);

    this.levelText = new Text({
      text: 'Lv.1',
      style: {
        fill: STYLES.level.textColor,
        fontSize: STYLES.level.fontSize,
        fontFamily: STYLES.level.fontFamily,
        fontWeight: 'bold',
      },
    });
    this.levelText.anchor.set(0.5, 0.5);
    this.levelBadge.addChild(this.levelText);

    // Create XP progress bar (below name row)
    this.xpBarContainer = new Container();
    this.addChild(this.xpBarContainer);

    this.xpBarBg = new Graphics();
    this.xpBarContainer.addChild(this.xpBarBg);

    this.xpBarFill = new Graphics();
    this.xpBarContainer.addChild(this.xpBarFill);

    // Set avatar if URL provided
    if (options.avatarUrl) {
      this.setAvatar(options.avatarUrl);
    }

    // Subscribe to state updates
    this.setupSubscriptions();

    // Initial layout
    this.updateAvatar();
    this.updateBalanceDisplay();
    this.updateGoldDisplay();
    this.updateTicketDisplay();
    this.updateLevelBadge();
    this.updateXpBar();
    this.layout();
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /**
   * Set player name
   */
  setName(name: string): void {
    this.currentName = name;
    this.nameText.text = name;
    this.updateAvatar();
    this.layout();
  }

  /**
   * Get current player name
   */
  getName(): string {
    return this.currentName;
  }

  /**
   * Set avatar from URL
   */
  setAvatar(url: string): void {
    // Load sprite from URL
    const texture = Texture.from(url);
    if (this.avatarSprite) {
      this.avatarSprite.texture = texture;
    } else {
      this.avatarSprite = new Sprite(texture);
      this.avatarSprite.width = this.avatarSize;
      this.avatarSprite.height = this.avatarSize;
      this.avatarSprite.anchor.set(0.5, 0.5);
      this.avatarContainer.addChild(this.avatarSprite);
    }

    // Apply circular mask to clip avatar to circle shape
    const innerRadius = this.avatarSize / 2 - STYLES.avatar.borderWidth;
    if (!this.avatarMask) {
      this.avatarMask = new Graphics();
      this.avatarContainer.addChild(this.avatarMask);
    }
    this.avatarMask.clear();
    this.avatarMask.circle(0, 0, innerRadius);
    this.avatarMask.fill(0xffffff);
    this.avatarSprite.mask = this.avatarMask;

    // Hide placeholder when sprite is loaded
    this.avatarText.visible = false;
    this.updateAvatar();
  }

  /**
   * Set balance value directly
   */
  setBalance(value: number): void {
    this.balanceState.value = value;
    this.balanceState.loading = false;
    this.balanceState.error = false;
    this.updateBalanceDisplay();
  }

  /**
   * Set balance loading state
   */
  setBalanceLoading(loading: boolean): void {
    this.balanceState.loading = loading;
    if (loading) {
      this.balanceState.error = false;
    }
    this.updateBalanceDisplay();
  }

  /**
   * Set balance error state
   */
  setBalanceError(error: boolean): void {
    this.balanceState.error = error;
    this.balanceState.loading = false;
    this.updateBalanceDisplay();
  }

  /**
   * Set player level and update badge
   */
  setLevel(level: number): void {
    this.currentLevel = level;
    const isMobile = this.screenWidth < STYLES.breakpoints.mobile;
    this.levelText.text = isMobile ? String(level) : `Lv.${String(level)}`;
    this.updateLevelBadge();
    this.layout();
  }

  /**
   * Set XP progress percentage (0-100) and update bar
   */
  setXPProgress(pct: number): void {
    this.currentXpPct = Math.max(0, Math.min(100, pct));
    this.updateXpBar();
  }

  /**
   * Resize for responsive layout
   */
  resize(screenWidth: number): void {
    this.screenWidth = screenWidth;
    this.updateBalanceDisplay();
    this.updateGoldDisplay();
    this.updateTicketDisplay();
    this.updateLevelBadge();
    this.updateXpBar();
    this.layout();
  }

  /**
   * Clean up subscriptions
   */
  destroy(): void {
    this.unsubscribes.forEach((unsub) => { unsub(); });
    this.unsubscribes = [];
    super.destroy();
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private setupSubscriptions(): void {
    // Subscribe to player display name (nickname from profile, falling back to auth name)
    const unsubName = appState.playerDisplayName.subscribe((name) => {
      this.setName(name);
    });
    this.unsubscribes.push(unsubName);

    // Subscribe to player chips (balance) updates
    const unsubChips = appState.playerChips.subscribe((chips) => {
      this.balanceState.value = chips;
      this.balanceState.loading = false;
      this.balanceState.error = false;
      this.updateBalanceDisplay();
    });
    this.unsubscribes.push(unsubChips);

    // Subscribe to player gold updates
    const unsubGold = appState.playerGold.subscribe((gold) => {
      this.goldValue = gold;
      this.updateGoldDisplay();
    });
    this.unsubscribes.push(unsubGold);

    // Subscribe to player tickets updates
    const unsubTickets = appState.playerTickets.subscribe((tickets) => {
      this.ticketValue = tickets;
      this.updateTicketDisplay();
    });
    this.unsubscribes.push(unsubTickets);

    // Subscribe to balance updates from ACC (handles same-value updates)
    accountService.on('balanceUpdated', (state) => {
      this.balanceState.value = state.chips;
      this.balanceState.loading = false;
      this.balanceState.error = false;
      this.updateBalanceDisplay();
      this.goldValue = state.gold;
      this.updateGoldDisplay();
      this.ticketValue = state.tickets;
      this.updateTicketDisplay();
    });

    // Subscribe to balance errors (e.g., ACC service not running)
    accountService.on('error', () => {
      this.balanceState.loading = false;
      this.balanceState.error = true;
      this.updateBalanceDisplay();
    });

    // Subscribe to balance loading state
    accountService.on('balanceLoading', () => {
      this.balanceState.loading = true;
      this.balanceState.error = false;
      this.updateBalanceDisplay();
    });

    // Subscribe to player level updates
    const unsubLevel = appState.playerLevel.subscribe((level) => {
      this.setLevel(level);
    });
    this.unsubscribes.push(unsubLevel);

    // Subscribe to XP progress updates
    const unsubXpPct = appState.playerXpPct.subscribe((pct) => {
      this.setXPProgress(pct);
    });
    this.unsubscribes.push(unsubXpPct);

    // Subscribe to hero avatar changes
    const unsubAvatar = appState.playerAvatarId.subscribe((avatarId) => {
      if (avatarId !== null) {
        this.setAvatar(getAvatarPath(avatarId));
      }
    });
    this.unsubscribes.push(unsubAvatar);
  }

  private layout(): void {
    const radius = this.avatarSize / 2;

    // Avatar position (centered at radius for circle drawing)
    this.avatarContainer.x = radius;
    this.avatarContainer.y = 0;

    // Name position (after avatar + gap)
    this.nameText.x = this.avatarSize + STYLES.avatar.gap;
    this.nameText.y = 0;

    // Level badge position (after name + gap)
    this.levelBadge.x = this.nameText.x + this.nameText.width + STYLES.level.gap;
    this.levelBadge.y = 0;

    // Balance position (after level badge + gap)
    this.balanceContainer.x = this.levelBadge.x + this.levelBadge.width + STYLES.name.gap;
    this.balanceContainer.y = 0;

    // Gold position (after balance + gap, only if visible)
    if (this.goldContainer.visible) {
      this.goldContainer.x = this.balanceContainer.x + this.balanceContainer.width + STYLES.gold.gap;
      this.goldContainer.y = 0;
    }

    // Ticket position (after gold or balance + gap, only if visible)
    if (this.ticketContainer.visible) {
      const lastPill = this.goldContainer.visible ? this.goldContainer : this.balanceContainer;
      const lastGap = this.goldContainer.visible ? STYLES.tickets.gap : STYLES.gold.gap;
      this.ticketContainer.x = lastPill.x + lastPill.width + lastGap;
      this.ticketContainer.y = 0;
    }

    // XP bar position (below name row, aligned with name)
    this.xpBarContainer.x = this.nameText.x;
    this.xpBarContainer.y = radius + STYLES.xpBar.gap;
  }

  private updateAvatar(): void {
    const radius = this.avatarSize / 2;

    // Draw avatar circle background
    this.avatarBg.clear();

    // Border
    this.avatarBg.circle(0, 0, radius);
    this.avatarBg.fill({ color: STYLES.avatar.borderColor });

    // Inner circle
    this.avatarBg.circle(0, 0, radius - STYLES.avatar.borderWidth);
    this.avatarBg.fill({
      color: STYLES.avatar.backgroundColor,
      alpha: STYLES.avatar.backgroundAlpha,
    });

    // Update placeholder text (first letter of name)
    if (!this.avatarSprite) {
      const firstLetter = this.currentName.charAt(0).toUpperCase() || '?';
      this.avatarText.text = firstLetter;
      this.avatarText.visible = true;
    }
  }

  private updateBalanceDisplay(): void {
    const isMobile = this.screenWidth < STYLES.breakpoints.mobile;
    const { loading, error, value } = this.balanceState;

    // Determine display text
    let displayText: string;
    let textAlpha = 1;

    if (loading) {
      displayText = '...';
    } else if (error) {
      displayText = '---';
      textAlpha = 0.5;
    } else {
      displayText = isMobile ? formatBalanceCompact(value) : formatBalance(value);
    }

    // Update text
    this.balanceText.text = displayText;
    this.balanceText.alpha = textAlpha;

    // Calculate pill dimensions
    const textWidth = Math.max(
      this.balanceText.width,
      STYLES.balance.minWidth - STYLES.balance.paddingX * 2
    );
    const pillWidth = textWidth + STYLES.balance.paddingX * 2;
    const pillHeight = STYLES.balance.fontSize + STYLES.balance.paddingY * 2;

    // Draw pill background (left-aligned, not centered)
    this.balanceBg.clear();
    this.balanceBg.roundRect(0, -pillHeight / 2, pillWidth, pillHeight, STYLES.balance.borderRadius);
    this.balanceBg.fill({
      color: STYLES.balance.backgroundColor,
      alpha: STYLES.balance.backgroundAlpha,
    });

    // Position text in center of pill
    this.balanceText.x = pillWidth / 2;
    this.balanceText.y = 0;

    // Re-layout after balance update (width may have changed)
    this.layout();
  }

  private updateGoldDisplay(): void {
    // Hide gold pill when player has no gold
    if (this.goldValue <= 0) {
      this.goldContainer.visible = false;
      this.layout();
      return;
    }

    this.goldContainer.visible = true;

    const isMobile = this.screenWidth < STYLES.breakpoints.mobile;
    const displayText = isMobile ? formatGoldCompact(this.goldValue) : formatGold(this.goldValue);

    // Update text
    this.goldText.text = displayText;

    // Calculate pill dimensions
    const textWidth = Math.max(
      this.goldText.width,
      STYLES.gold.minWidth - STYLES.gold.paddingX * 2
    );
    const pillWidth = textWidth + STYLES.gold.paddingX * 2;
    const pillHeight = STYLES.gold.fontSize + STYLES.gold.paddingY * 2;

    // Draw pill background
    this.goldBg.clear();
    this.goldBg.roundRect(0, -pillHeight / 2, pillWidth, pillHeight, STYLES.gold.borderRadius);
    this.goldBg.fill({
      color: STYLES.gold.backgroundColor,
      alpha: STYLES.gold.backgroundAlpha,
    });

    // Position text in center of pill
    this.goldText.x = pillWidth / 2;
    this.goldText.y = 0;

    // Re-layout after gold update (width may have changed)
    this.layout();
  }

  private updateTicketDisplay(): void {
    // Hide ticket pill when player has no tickets
    if (this.ticketValue <= 0) {
      this.ticketContainer.visible = false;
      this.layout();
      return;
    }

    this.ticketContainer.visible = true;

    const isMobile = this.screenWidth < STYLES.breakpoints.mobile;
    const displayText = isMobile ? formatTicketsCompact(this.ticketValue) : formatTickets(this.ticketValue);

    // Update text
    this.ticketText.text = displayText;

    // Calculate pill dimensions
    const textWidth = Math.max(
      this.ticketText.width,
      STYLES.tickets.minWidth - STYLES.tickets.paddingX * 2
    );
    const pillWidth = textWidth + STYLES.tickets.paddingX * 2;
    const pillHeight = STYLES.tickets.fontSize + STYLES.tickets.paddingY * 2;

    // Draw pill background
    this.ticketBg.clear();
    this.ticketBg.roundRect(0, -pillHeight / 2, pillWidth, pillHeight, STYLES.tickets.borderRadius);
    this.ticketBg.fill({
      color: STYLES.tickets.backgroundColor,
      alpha: STYLES.tickets.backgroundAlpha,
    });

    // Position text in center of pill
    this.ticketText.x = pillWidth / 2;
    this.ticketText.y = 0;

    // Re-layout after ticket update (width may have changed)
    this.layout();
  }

  private updateLevelBadge(): void {
    const isMobile = this.screenWidth < STYLES.breakpoints.mobile;
    this.levelText.text = isMobile ? String(this.currentLevel) : `Lv.${String(this.currentLevel)}`;

    // Calculate badge dimensions
    const textWidth = this.levelText.width;
    const pillWidth = textWidth + STYLES.level.paddingX * 2;
    const pillHeight = STYLES.level.fontSize + STYLES.level.paddingY * 2;

    // Draw badge background
    this.levelBg.clear();
    this.levelBg.roundRect(0, -pillHeight / 2, pillWidth, pillHeight, STYLES.level.borderRadius);
    this.levelBg.fill({
      color: STYLES.level.backgroundColor,
      alpha: STYLES.level.backgroundAlpha,
    });

    // Center text in badge
    this.levelText.x = pillWidth / 2;
    this.levelText.y = 0;
  }

  private updateXpBar(): void {
    const isMobile = this.screenWidth < STYLES.breakpoints.mobile;
    const maxWidth = isMobile ? STYLES.xpBar.maxWidthMobile : STYLES.xpBar.maxWidth;
    const barHeight = STYLES.xpBar.height;

    // Draw track (background)
    this.xpBarBg.clear();
    this.xpBarBg.roundRect(0, 0, maxWidth, barHeight, STYLES.xpBar.borderRadius);
    this.xpBarBg.fill({
      color: STYLES.xpBar.trackColor,
      alpha: STYLES.xpBar.trackAlpha,
    });

    // Draw fill (progress)
    const fillWidth = Math.max(0, (this.currentXpPct / 100) * maxWidth);
    if (fillWidth > 0) {
      const fillColor = this.currentXpPct >= 90
        ? STYLES.xpBar.fillColorAlmost
        : STYLES.xpBar.fillColor;
      this.xpBarFill.clear();
      this.xpBarFill.roundRect(0, 0, fillWidth, barHeight, STYLES.xpBar.borderRadius);
      this.xpBarFill.fill(fillColor);
    } else {
      this.xpBarFill.clear();
    }
  }
}
